/**
 * antd 主题 token：全项目唯一允许写颜色字面量的地方（src/theme/）。
 * 亮色沿用旧管理中台 src/styles/theme.ts 的黑白灰取值；
 * 暗色只给主色等少量 seed，其余由 darkAlgorithm 推导。
 * 页面和组件的自定义样式只能引用 antd 输出的 var(--ant-*) 变量。
 */
import { theme, type ThemeConfig } from 'antd'
import type { AppliedTheme } from '@/stores/theme'

// 与颜色无关、亮暗共用的 token
const sharedTokens: ThemeConfig['token'] = {
  colorSuccess: '#52c41a',
  colorWarning: '#faad14',
  colorError: '#f5222d',
  colorInfo: '#1890ff',

  fontFamily: `-apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC',
    'Hiragino Sans GB', 'Microsoft YaHei', 'Helvetica Neue',
    Helvetica, Arial, sans-serif`,
  fontSize: 14,
  fontSizeSM: 12,
  fontSizeLG: 16,
  fontSizeXL: 20,
  fontSizeHeading1: 32,
  fontSizeHeading2: 24,
  fontSizeHeading3: 20,
  fontSizeHeading4: 18,
  fontSizeHeading5: 16,

  borderRadius: 6,
  borderRadiusLG: 8,
  borderRadiusSM: 4,
  borderRadiusXS: 2,

  controlHeight: 40,
  controlHeightSM: 32,
  controlHeightLG: 48
}

const lightTokens: ThemeConfig['token'] = {
  ...sharedTokens,
  colorPrimary: '#333333',

  colorText: '#424242',
  colorTextSecondary: '#757575',
  colorTextTertiary: '#9e9e9e',
  colorTextQuaternary: '#bdbdbd',

  colorBgContainer: '#ffffff',
  colorBgLayout: '#f5f5f5',
  colorBgElevated: '#ffffff',
  colorBgSpotlight: '#424242',

  colorBorder: '#e0e0e0',
  colorBorderSecondary: '#eeeeee',

  colorFill: '#f5f5f5',
  colorFillSecondary: '#eeeeee',
  colorFillTertiary: '#e0e0e0',
  colorFillQuaternary: '#bdbdbd',
  // antd 用 colorFillAlter 铺表头、描述列表标签列、子菜单背景；
  // 不单独设置时会继承上面的 #bdbdbd，灰底上的次要文字看不清
  colorFillAlter: '#fafafa',

  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
  boxShadowSecondary: '0 4px 16px rgba(0, 0, 0, 0.08)'
}

// 暗色主色用浅灰（#333333 在暗底上几乎看不见）
const DARK_PRIMARY_COLOR = '#e0e0e0'

const darkTokens: ThemeConfig['token'] = {
  ...sharedTokens,
  colorPrimary: DARK_PRIMARY_COLOR,
  // 主色是浅灰，主按钮、选中的单选按钮等「主色底上的文字」改用深色才看得清
  colorTextLightSolid: '#1a1a1a'
}

// 这几个组件的「浅色文字」不在主色底上，暗色下仍需白字
const darkComponents: ThemeConfig['components'] = {
  Tooltip: { colorTextLightSolid: '#ffffff' },
  Badge: { colorTextLightSolid: '#ffffff' }
}

// cssVar 使用固定 key，根节点带 acquilang 类，样式里可直接引用 --ant-* 变量
const CSS_VAR_KEY = 'acquilang'

function buildThemeConfig(appliedTheme: AppliedTheme): ThemeConfig {
  const isDark = appliedTheme === 'dark'
  const baseConfig: ThemeConfig = {
    algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: isDark ? darkTokens : lightTokens
  }
  // Layout 的 Header、Sider 默认是深蓝底，改为跟随容器背景
  const derived = theme.getDesignToken(baseConfig)
  // 亮色主色是深灰，由它推导的选中底色（colorPrimaryBg 等）太暗，菜单和表格选中行统一用浅灰底
  const lightSelectedBg = derived.colorFillTertiary
  // 暗色主色是浅灰，由它推导的 colorPrimaryBg 偏亮（#515151），行内红色、蓝色链接看不清；
  // 勾选行改用比容器稍亮的 colorBgElevated（#1f1f1f），错误色在它上面仍有 3:1 对比度，再亮就不够
  const tableSelectedBg = isDark ? derived.colorBgElevated : lightSelectedBg
  return {
    ...baseConfig,
    components: {
      ...(isDark ? darkComponents : {}),
      Layout: {
        headerBg: derived.colorBgContainer,
        siderBg: derived.colorBgContainer,
        bodyBg: derived.colorBgLayout,
        headerPadding: '0 24px'
      },
      // 侧边栏已有分隔线，菜单不再画右边框
      Menu: { activeBarBorderWidth: 0, ...(isDark ? {} : { itemSelectedBg: lightSelectedBg }) },
      // 勾选行默认用 colorPrimaryBg / colorPrimaryBgHover，由主色推导，亮暗下都看不清行内文字或链接
      Table: { rowSelectedBg: tableSelectedBg, rowSelectedHoverBg: tableSelectedBg }
    },
    cssVar: { prefix: 'ant', key: CSS_VAR_KEY }
  }
}

// 亮暗两套配置各算一次，切换主题时对象引用稳定
const THEME_CONFIGS: Record<AppliedTheme, ThemeConfig> = {
  light: buildThemeConfig('light'),
  dark: buildThemeConfig('dark')
}

export function getThemeConfig(appliedTheme: AppliedTheme): ThemeConfig {
  return THEME_CONFIGS[appliedTheme]
}
